import { BaseGamePredictionRecordsByGameID, IGameStats, SeasonID, SeasonWeekID } from '@shared/types';
import { writePredictionBatches } from 'api/PredictionBatchWriter';
import { matchupBoxScoreConverter } from 'api/converters/MatchBoxScoreConverter';
import { matchupConverter } from 'api/converters/MatchScheduleConverter';
import { ActionCls, Cls, Emojis, END, ValueCls } from 'app/constants';
import { collection, doc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { breakSeasonWeekID } from 'helpers/IdHelpers';
import { GameRecordT, ILineScore, IMatchup } from 'types';
import { db, functions } from '../lib/firebase';

// #region SCHEDULE PREDICTION ADDITION ACTION ********************************
export const savePredictionsToSchedule = async (
  predictions: BaseGamePredictionRecordsByGameID,
  yearID: SeasonID
): Promise<IMatchup[]> => {
  const path = `stats/${yearID}/schedule`;

  const gamesRecord: GameRecordT = await fetchScheduleAsGameRecordT(path);
  const matchupsWithPrediction: IMatchup[] = [];

  // Fetch existing predictions for all games to preserve them
  for (const gameID of Object.keys(predictions)) {
    const gm = gamesRecord[gameID];
    if (!gm) {
      throw new Error(`Game not found for ${gameID}; schedule predictions were not saved`);
    } else {
      const existingPred = gm.predictions || {};
      const lineScore = gm.lineScore || ({} as ILineScore);
      const prediction = predictions[gameID];

      const gmWithPred = { ...gm, lineScore, predictions: { ...existingPred, [prediction.modelVersion]: prediction } };
      matchupsWithPrediction.push(gmWithPred);
    }
  }

  const msg = `${Emojis.eyes} %c BATCH WRITING %c %c IGamePredictionBase %c to %c ${path} %c`;
  console.log(msg, Cls.Act, '', Cls.Info, '', Cls.Loc, '');
  await writePredictionBatches(matchupsWithPrediction, (batch, game) => {
    // Merge: preserve existing predictions and add/update the new one.
    batch.set(doc(db, path, game.gameID), game, { merge: true });
  });

  // Publish only after every schedule batch commits. The backend updates nested
  // schedule freshness without advancing the statistics workflow timestamp.
  const seasonWeekIDs = [...new Set(matchupsWithPrediction.map((game) => game.seasonWeekID))];
  if (seasonWeekIDs.length > 0) {
    await httpsCallable(functions, 'publishScheduleFreshness')({ seasonWeekIDs });
  }

  return matchupsWithPrediction;
};

// export const cleanMatchupRecords = async (yearID: SeasonID): Promise<void> => {
//   const path = `stats/${yearID}/schedule`;
//   const games = await fetchMatchSchedule(yearID);

//   const cleanupBatch = writeBatch(db);
//   games.forEach((game) => {
//     if (Object.hasOwn(game, 'predictions.1.2.2')) {
//       delete (game as any)['predictions.1.2.2'];
//     }
//     if (Object.hasOwn(game, 'gameContext')) {
//       const matchRef = doc(db, path, game.gameID);
//       cleanupBatch.set(matchRef, { ...game });
//     } else {
//       console.log(`No gameContext found for gameID: ${game.gameID}`);
//     }
//   });
//   await cleanupBatch.commit();
// };

// #endregion

// #region SCHEDULE ACTIONS ***************************************************
export const saveSchedule = async (matchSchedule: IMatchup[], collectionPath: string): Promise<void> => {
  const batch = writeBatch(db);

  matchSchedule.forEach((match) => {
    const matchRef = doc(db, collectionPath, match.gameID).withConverter(matchupConverter);
    batch.set(matchRef, { ...match });
  });

  console.log(`${Emojis.eyes} %c BATCH WRITING ${END} %c ${collectionPath} ${END}`, ActionCls, ValueCls);

  return await batch.commit();
};

export const saveMatchSchedule = async (matchSchedule: IMatchup[], year: SeasonID = '2024'): Promise<void> => {
  const collectionPath = `stats/${year}/schedule`;
  return await saveSchedule(matchSchedule, collectionPath);
};

export const saveOldMatchSchedule = async (matchSchedule: IMatchup[], year: SeasonID = '2024'): Promise<void> => {
  const collectionPath = `matchschedule${year}`;
  return await saveSchedule(matchSchedule, collectionPath);
};

export const fetchSchedule = async (collectionPath: string): Promise<IMatchup[]> => {
  const collectionData = collection(db, collectionPath).withConverter(matchupConverter);
  const snapshot = await getDocs(collectionData);
  const matches: IMatchup[] = [];
  snapshot.forEach((doc) => {
    const match = doc.data();
    matches.push(match);
  });
  return matches;
};

export const fetchScheduleAsGameRecordT = async (collectionPath: string): Promise<GameRecordT> => {
  const collectionData = collection(db, collectionPath).withConverter(matchupConverter);
  const snapshot = await getDocs(collectionData);
  const gameRecs: GameRecordT = {};
  snapshot.forEach((doc) => {
    gameRecs[doc.id] = doc.data();
  });
  return gameRecs;
};

export const fetchMatchScheduleBySeasonWeekID = async (seasonWeekID: SeasonWeekID): Promise<IMatchup[]> => {
  const { yearID } = breakSeasonWeekID(seasonWeekID);
  const path = `stats/${yearID}/schedule`;

  const scheduleQuery = query(collection(db, path), where('seasonWeekID', '==', seasonWeekID));
  const scheduleSnapshot = await getDocs(scheduleQuery);
  const matches: IMatchup[] = [];
  scheduleSnapshot.forEach((doc) => {
    const match = doc.data() as IMatchup;
    matches.push(match);
  });
  return matches;
};

export const fetchMatchSchedule = async (year: SeasonID = '2024'): Promise<IMatchup[]> => {
  const collectionPath = `stats/${year}/schedule`;
  return await fetchSchedule(collectionPath);
};

export const fetchOldMatchSchedule = async (year: SeasonID = '2024'): Promise<IMatchup[]> => {
  const collectionPath = `matchschedule${year}`;
  return await fetchSchedule(collectionPath);
};
// #endregion

// #region MATCH RESULTS ACTIONS **********************************************
export const saveGameResults = async (matchesBoxScores: IGameStats[], collectionPath: string): Promise<void> => {
  const batch = writeBatch(db);

  matchesBoxScores.forEach((matchResult) => {
    const matchRef = doc(db, collectionPath, matchResult.gameID).withConverter(matchupBoxScoreConverter);
    batch.set(matchRef, { ...matchResult });
  });

  console.log(`${Emojis.eyes} %c BATCH WRITING ${END} %c ${collectionPath} ${END}`, ActionCls, ValueCls);

  return await batch.commit();
};

export const saveMatchResults = async (matchesBoxScores: IGameStats[], year: SeasonID): Promise<void> => {
  return await saveGameResults(matchesBoxScores, `stats/${year}/matchresults`);
};

export const saveOldMatchResults = async (matchesBoxScores: IGameStats[], year: SeasonID): Promise<void> => {
  return await saveGameResults(matchesBoxScores, `matchresults${year}`);
};

export const fetchResults = async (collectionPath: string): Promise<IGameStats[]> => {
  const collectionData = collection(db, collectionPath).withConverter(matchupBoxScoreConverter);
  const snapshot = await getDocs(collectionData);
  const matches: IGameStats[] = [];
  snapshot.forEach((doc) => {
    const match = doc.data();
    matches.push(match);
  });
  return matches;
};

export const fetchMatchResults = async (year: SeasonID = '2024'): Promise<IGameStats[]> => {
  const collectionName = `stats/${year}/matchresults`;
  return await fetchResults(collectionName);
};

export const fetchOldMatchResults = async (year: SeasonID = '2024'): Promise<IGameStats[]> => {
  const collectionName = `matchresults${year}`;
  return await fetchResults(collectionName);
};
// #endregion
